import React, { useState } from 'react'
import './trade-portal-detail-modal.styles.scss'
import Modal from '@/components/modal/modal.component'
import { ModalAlert, ModalConfirm } from '@/components/modal/modal.component'
import { UseCloudGameTransactions } from '@/utils/firebase.utils'
import ThreeDotsLoader from '@/components/loader/three-dots-loader.component'
import TradePortalOfferModal from './trade-portal-offer-modal.component'
import { format } from 'date-fns'
import ImageContainer from '../image-container/image-container.component'

const TradePortalDetailModal = ({
  show,
  cancel,
  post,
  gameData,
  playerData,
  gameElementsObj,
  gameElements,
  isManager,
}) => {
  const [loadingOfferId, setLoadingOfferId] = useState(null)
  const [loadingAction, setLoadingAction] = useState(null)
  const [alertMessage, setAlertMessage] = useState(null)
  const [confirmMessage, setConfirmMessage] = useState(null)
  const [pendingAction, setPendingAction] = useState(null)
  const [showOfferModal, setShowOfferModal] = useState(false)

  if (!post) return null

  const isOwnPost = post.posterId === playerData?.playerId
  const offeringEl = gameElements?.[post.offering?.itemId]

  const myExistingOffer = playerData
    ? Object.values(post.offers ?? {}).find((o) => o.offererId === playerData.playerId)
    : null

  const sortedOffers = Object.values(post.offers ?? {}).sort((a, b) => {
    const order = { approved: 0, pending: 1, declined: 2 }
    return (order[a.status] ?? 1) - (order[b.status] ?? 1)
  })

  function getItemLabel(item) {
    if (item.type === 'currency') return `${item.quantity} Currency`
    if (item.type === 'points') return `${item.quantity} Points`
    const el = gameElements?.[item.itemId]
    const name = el?.name ?? item.itemId
    return item.quantity > 1 ? `${item.quantity}x ${name}` : name
  }

  function formatTs(ts) {
    if (!ts) return ''
    try {
      const d = ts.toDate ? ts.toDate() : new Date(ts)
      return format(d, 'MMM d, h:mm a')
    } catch {
      return ''
    }
  }

  function confirmThen(message, action) {
    setConfirmMessage(message)
    setPendingAction(() => action)
  }

  async function respondToOffer(offerId, response) {
    setLoadingOfferId(offerId)
    const res = await UseCloudGameTransactions('respondToTradeOffer', {
      gameId: gameData.gameId,
      playerId: playerData.playerId,
      tradeId: post.id,
      offerId,
      response,
    })
    setLoadingOfferId(null)
    if (res?.error) {
      setAlertMessage(res.error)
    } else if (response === 'approved' && !res?.awaiting) {
      cancel()
    }
  }

  async function completeTradePortalTransaction(offerId) {
    setLoadingAction('complete')
    const res = await UseCloudGameTransactions('completeTradePortalTransaction', {
      gameId: gameData.gameId,
      tradeId: post.id,
      offerId,
    })
    setLoadingAction(null)
    if (res?.error) {
      setAlertMessage(res.error)
    } else {
      cancel()
    }
  }

  async function cancelPost() {
    setLoadingAction('cancel')
    const res = await UseCloudGameTransactions('cancelTradePost', {
      gameId: gameData.gameId,
      playerId: playerData?.playerId ?? null,
      tradeId: post.id,
      isManager: isManager ?? false,
    })
    setLoadingAction(null)
    if (res?.error) {
      setAlertMessage(res.error)
    } else {
      cancel()
    }
  }

  async function reverseTradePortalTransaction() {
    setLoadingAction('reverse')
    const res = await UseCloudGameTransactions('reverseTradePortalTransaction', {
      gameId: gameData.gameId,
      tradeId: post.id,
    })
    setLoadingAction(null)
    if (res?.error) {
      setAlertMessage(res.error)
    } else {
      cancel()
    }
  }

  return (
    <>
      <Modal
        show={show && !showOfferModal}
        cancel={cancel}
        text='Trade Details'
        closeButton={true}
        cls='trade-detail-modal'
      >
        

        <div className='trade-detail-modal-content'>

          {/* Post summary */}
          <div className='post-summary-box'>
            <div className='summary-row'>
              <span className='summary-label'>Posted by</span>
              <div className='summary-value'>{post.posterName}</div>
            </div>
            <div className='summary-row'>
              <span className='summary-label'>Offering</span>
              <div className='summary-value'>
                {offeringEl?.icon && (
                  <ImageContainer src={offeringEl.icon} alt={offeringEl.name}
                    style={{ width: 30, height: 30, objectFit: 'contain', borderRadius: '0.25rem' }} />
                )}
                {post.offering?.quantity > 1 ? `${post.offering.quantity}x ` : ''}
                {offeringEl?.name ?? 'Item'}
              </div>
            </div>
            <div className='summary-row'>
              <span className='summary-label'>Wants</span>
              <div className='summary-value'>
                {post.requesting?.length > 0
                  ? post.requesting.map((r, i) => (
                    <span key={i} className='want-chip'>{getItemLabel(r)}</span>
                  ))
                  : <span style={{ color: '#aaa', fontStyle: 'italic' }}>Open to offers</span>
                }
              </div>
            </div>
          </div>

          {/* ─── Pending post: poster or manager sees offers ─── */}
          {post.status === 'pending' && (isOwnPost || isManager) && (
            <>
              <span className='offers-heading'>
                Offers ({sortedOffers.length})
              </span>
              {sortedOffers.length === 0 ? (
                <span className='no-offers-msg'>No offers yet.</span>
              ) : (
                sortedOffers.map((offer) => (
                  <div
                    key={offer.offerId}
                    className={`offer-card offer-${offer.status}`}
                  >
                    <div className='offer-header'>
                      <span className='offerer-name'>{offer.offererName}</span>
                      <span className='offer-ts'>{formatTs(offer.ts_created)}</span>
                    </div>
                    <div className='offer-chips'>
                      {offer.items?.length > 0
                        ? offer.items.map((item, i) => (
                          <span key={i} className='offer-chip'>
                            {getItemLabel(item)}
                          </span>
                        ))
                        : <span className='nothing-offered'>No items offered</span>
                      }
                    </div>
                    {isOwnPost && offer.status === 'pending' && (
                      <div className='offer-actions'>
                        <button
                          className='g-button small-btn'
                          disabled={loadingOfferId === offer.offerId}
                          onClick={() =>
                            confirmThen(
                              `Approve this offer from <b>${offer.offererName}</b>?`,
                              () => respondToOffer(offer.offerId, 'approved')
                            )
                          }
                        >
                          {loadingOfferId === offer.offerId
                            ? <ThreeDotsLoader />
                            : 'Approve'
                          }
                        </button>
                        <button
                          className='g-button small-btn'
                          disabled={loadingOfferId === offer.offerId}
                          onClick={() => respondToOffer(offer.offerId, 'declined')}
                        >
                          Decline
                        </button>
                      </div>
                    )}
                    {offer.status !== 'pending' && (
                      <div className='offer-actions'>
                        <span className={`offer-status-label ${offer.status}`}>
                          {offer.status}
                        </span>
                      </div>
                    )}
                  </div>
                ))
              )}
            </>
          )}

          {/* ─── Pending post: browsing player ─── */}
          {post.status === 'pending' && !isOwnPost && !isManager && (
            myExistingOffer ? (
              <>
                <span className='offers-heading'>Your Offer</span>
                <div className={`offer-card offer-${myExistingOffer.status}`}>
                  <div className='offer-chips'>
                    {myExistingOffer.items?.length > 0
                      ? myExistingOffer.items.map((item, i) => (
                        <span key={i} className='offer-chip'>
                          {getItemLabel(item)}
                        </span>
                      ))
                      : <span className='nothing-offered'>No items offered</span>
                    }
                  </div>
                  <div className='offer-actions'>
                    <span className={`offer-status-label ${myExistingOffer.status}`}>
                      {myExistingOffer.status}
                    </span>
                  </div>
                </div>
              </>
            ) : null
          )}

          {/* ─── Awaiting manager approval ─── */}
          {post.status === 'awaiting_manager' && (
            <div className='detail-status-msg awaiting'>
              {isManager
                ? 'An offer has been approved by the poster and is awaiting your confirmation.'
                : 'An offer has been approved and is awaiting manager confirmation.'
              }
            </div>
          )}

          {post.status === 'awaiting_manager' && isManager && post.approvedOfferId && (
            (() => {
              const approvedOffer = post.offers?.[post.approvedOfferId]
              if (!approvedOffer) return null
              return (
                <>
                  <span className='offers-heading'>Approved Offer</span>
                  <div className='offer-card offer-approved'>
                    <div className='offer-header'>
                      <span className='offerer-name'>{approvedOffer.offererName}</span>
                    </div>
                    <div className='offer-chips'>
                      {approvedOffer.items?.length > 0
                        ? approvedOffer.items.map((item, i) => (
                          <span key={i} className='offer-chip'>
                            {getItemLabel(item)}
                          </span>
                        ))
                        : <span className='nothing-offered'>No items offered</span>
                      }
                    </div>
                  </div>
                </>
              )
            })()
          )}

          {/* ─── Completed ─── */}
          {post.status === 'completed' && (() => {
            const approvedOffer = post.approvedOfferId ? post.offers?.[post.approvedOfferId] : null
            if (!approvedOffer) return (
              <div className='detail-status-msg completed'>Trade completed successfully.</div>
            )
            const declinedOffers = sortedOffers.filter(o => o.status === 'declined')
            return (
              <>
                <div className='tp-trade-completion'>
                  <div className='tp-completion-header'>🎉 Trade completed!</div>
                  <div className='tp-completion-columns'>
                    <div className='tp-completion-player'>
                      <span className='tp-completion-name'>{post.posterName}</span>
                      <span className='tp-completion-label'>received</span>
                      <div className='tp-completion-items'>
                        {approvedOffer.items?.length > 0
                          ? approvedOffer.items.map((item, i) => {
                              const el = item.itemId ? gameElements?.[item.itemId] : null
                              const label = getItemLabel(item)
                              const icon = el?.icon
                                ?? (item.type === 'currency' ? '/images/icons/coin.png'
                                  : item.type === 'points' ? '/images/icons/xp.png'
                                  : null)
                              return (
                                <div key={i} className='tp-completion-item-card' title={label}>
                                  {icon && <ImageContainer src={icon} alt='' className='tp-completion-item-icon' />}
                                  <span className='tp-completion-item-name'>{label}</span>
                                </div>
                              )
                            })
                          : <span className='no-offers-msg'>Nothing</span>
                        }
                      </div>
                    </div>
                    <div className='tp-completion-player'>
                      <span className='tp-completion-name'>{approvedOffer.offererName}</span>
                      <span className='tp-completion-label'>received</span>
                      <div className='tp-completion-items'>
                        {(() => {
                          const label = getItemLabel(post.offering)
                          return (
                            <div className='tp-completion-item-card' title={label}>
                              {offeringEl?.icon && <ImageContainer src={offeringEl.icon} alt='' className='tp-completion-item-icon' />}
                              <span className='tp-completion-item-name'>{label}</span>
                            </div>
                          )
                        })()}
                      </div>
                    </div>
                  </div>
                </div>

                {declinedOffers.length > 0 && (
                  <>
                    <span className='offers-heading'>
                      Declined Offers ({declinedOffers.length})
                    </span>
                    {declinedOffers.map(offer => (
                      <div key={offer.offerId} className='offer-card offer-declined'>
                        <div className='offer-header'>
                          <span className='offerer-name'>{offer.offererName}</span>
                          <span className='offer-ts'>{formatTs(offer.ts_created)}</span>
                        </div>
                        <div className='offer-chips'>
                          {offer.items?.length > 0
                            ? offer.items.map((item, i) => (
                                <span key={i} className='offer-chip'>{getItemLabel(item)}</span>
                              ))
                            : <span className='nothing-offered'>No items offered</span>
                          }
                        </div>
                      </div>
                    ))}
                  </>
                )}
              </>
            )
          })()}

          {/* ─── Cancelled ─── */}
          {post.status === 'cancelled' && (
            <div className='detail-status-msg cancelled'>
              This trade was cancelled.
            </div>
          )}

          {/* ─── Reversed ─── */}
          {post.status === 'reversed' && (() => {
            const approvedOffer = post.approvedOfferId ? post.offers?.[post.approvedOfferId] : null
            if (!approvedOffer) return (
              <div className='detail-status-msg reversed'>This trade was reversed.</div>
            )
            return (
              <div className='tp-trade-completion reversed'>
                <div className='tp-completion-header'>↩ Trade reversed — items returned</div>
                <div className='tp-completion-columns'>
                  <div className='tp-completion-player'>
                    <span className='tp-completion-name'>{post.posterName}</span>
                    <span className='tp-completion-label'>got back</span>
                    <div className='tp-completion-items'>
                      {(() => {
                        const label = getItemLabel(post.offering)
                        return (
                          <div className='tp-completion-item-card' title={label}>
                            {offeringEl?.icon && <ImageContainer src={offeringEl.icon} alt='' className='tp-completion-item-icon' />}
                            <span className='tp-completion-item-name'>{label}</span>
                          </div>
                        )
                      })()}
                    </div>
                  </div>
                  <div className='tp-completion-player'>
                    <span className='tp-completion-name'>{approvedOffer.offererName}</span>
                    <span className='tp-completion-label'>got back</span>
                    <div className='tp-completion-items'>
                      {approvedOffer.items?.length > 0
                        ? approvedOffer.items.map((item, i) => {
                            const el = item.itemId ? gameElements?.[item.itemId] : null
                            const label = getItemLabel(item)
                            const icon = el?.icon
                              ?? (item.type === 'currency' ? '/images/icons/coin.png'
                                : item.type === 'points' ? '/images/icons/xp.png'
                                : null)
                            return (
                              <div key={i} className='tp-completion-item-card' title={label}>
                                {icon && <ImageContainer src={icon} alt='' className='tp-completion-item-icon' />}
                                <span className='tp-completion-item-name'>{label}</span>
                              </div>
                            )
                          })
                        : <span className='no-offers-msg'>Nothing</span>
                      }
                    </div>
                  </div>
                </div>
              </div>
            )
          })()}

          {/* Footer actions */}
          <div className='detail-footer'>
            {/* Other player: make offer button */}
            {post.status === 'pending' && !isOwnPost && !isManager && !myExistingOffer && (
              <button
                className='g-button med-btn primary'
                onClick={() => setShowOfferModal(true)}
              >
                Make an Offer
              </button>
            )}

            {/* Manager: complete awaiting_manager */}
            {isManager && post.status === 'awaiting_manager' && (
              <button
                className='g-button med-btn primary'
                disabled={loadingAction === 'complete'}
                onClick={() =>
                  confirmThen(
                    'Complete this trade? Items will be transferred immediately.',
                    () => completeTradePortalTransaction(post.approvedOfferId)
                  )
                }
              >
                {loadingAction === 'complete' ? <ThreeDotsLoader /> : 'Complete Trade'}
              </button>
            )}

            {/* Manager or poster: cancel pending/awaiting */}
            {(isManager || isOwnPost) &&
              ['pending', 'awaiting_manager'].includes(post.status) && (
                <button
                  className='g-button med-btn'
                  disabled={loadingAction === 'cancel'}
                  onClick={() =>
                    confirmThen(
                      'Cancel this trade post? All pending offers will be declined.',
                      cancelPost
                    )
                  }
                >
                  {loadingAction === 'cancel' ? <ThreeDotsLoader /> : 'Cancel Post'}
                </button>
              )}

            {/* Manager: reverse completed trade */}
            {isManager && post.status === 'completed' && (
              <button
                className='g-button med-btn'
                disabled={loadingAction === 'reverse'}
                onClick={() =>
                  confirmThen(
                    'Reverse this trade? All items will be returned to their original owners.',
                    reverseTradePortalTransaction
                  )
                }
              >
                {loadingAction === 'reverse' ? <ThreeDotsLoader /> : 'Reverse Trade'}
              </button>
            )}

            <button className='g-button med-btn' onClick={cancel}>
              Close
            </button>
          </div>
        </div>
      </Modal>

      {/* Offer modal mounts on top; detail modal hides until it closes */}
      <TradePortalOfferModal
        show={showOfferModal}
        cancel={() => setShowOfferModal(false)}
        post={post}
        gameData={gameData}
        playerData={playerData}
        gameElements={gameElements}
        onSuccess={() => {
          setShowOfferModal(false)
          cancel()
        }}
      />
      <ModalAlert
          show={!!alertMessage}
          cancel={() => setAlertMessage(null)}
          message={alertMessage}
        />
        <ModalConfirm
          show={!!confirmMessage}
          cancel={() => { setConfirmMessage(null); setPendingAction(null) }}
          onApprove={() => { setConfirmMessage(null); pendingAction?.() }}
          message={confirmMessage}
        />
    </>
  )
}

export default TradePortalDetailModal
